import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { VehicleService } from '../../vehicle/vehicle.service';
import { Vehicle } from '../../vehicle/vehicle.model';
import { Driver } from '../driver.model';
import { DriverService } from '../driver.service';
import { SessionStorageService } from "../../../services/session-storage.service";
import { Country } from "../../../models/common/country.model";
import { City } from "../../../models/common/city.model";
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from "primeng/autocomplete";
import { CommonModule } from '@angular/common';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { ProgressBarModule } from "primeng/progressbar";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { CompanyService } from '../../../company/company.service';
import { TransportationAgencyService } from '../../../company/transportation-agency/transportation-agency.service';
import { TransportationAgency } from '../../../company/transportation-agency/transportation-agency.model';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { resolveSisterCompanyScope, sisterCompanyFleetAccessMessage } from '../../../shared/utils/sister-company-scope.util';
import { countryPhoneValidator, normalizePhoneForCountry, phoneMaskForCountry, phonePlaceholderForCountry } from '../../../shared/utils/country-phone.util';
import { Location } from '../../../location/location.model';
import { LocationService } from '../../../location/location.service';
import { CrmClient } from '../../../crm/crm.model';
import { CrmService } from '../../../crm/crm.service';


@Component({
    selector: 'app-edit-driver',
    templateUrl: './edit-driver.component.html',
    styleUrls: ['./edit-driver.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        AutoCompleteModule,
        SelectModule,
        BreadcrumbModule,
        FileUploadModule,
        DatePickerModule,
        TextareaModule,
        ProgressBarModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ],
    providers: [
        MessageService,
        FirebaseStorageService,
        SessionStorageService,
        DriverService,
        VehicleService,
        BaseMasterDataService,
        LocationService
    ]
})
export class EditDriverComponent implements OnInit {

    @Input() embedded = false;
    @Input() embeddedSource: 'owned' | 'freelance' | 'rental' | 'transporter' = 'owned';
    @Output() saved = new EventEmitter<Driver>();
    @Output() cancelled = new EventEmitter<void>();

    createEditDriverForm: FormGroup<any>;
    nameInput: FormControl;
    phoneNumberInput: FormControl;
    emailAddressInput: FormControl;
    nationalityDropdown: FormControl;
    residingCountryDropdown: FormControl;
    residingCityDropdown: FormControl;
    homeLocationDropdown: FormControl;
    dateOfBirthInput: FormControl;
    genderDropdown: FormControl;
    identityTypeInput: FormControl;
    identityIssueNumberInput: FormControl;
    residenceNumberInput: FormControl;
    residenceExpiryDateInput: FormControl;
    licenseNumberInput: FormControl;
    licenseExpiryDateInput: FormControl;
    licenseCountryDropdown: FormControl;
    licenseClassInput: FormControl;
    licenseTypeInput: FormControl;
    transportPermitNumberInput: FormControl;
    transportPermitExpiryDateInput: FormControl;
    transportPermitCountryDropdown: FormControl;
    currentVehicleDropdown: FormControl;

    driverId: number;
    isLoading: boolean = false;
    loadError: string | null = null;
    driver: Driver = new Driver();
    countries: Country[] = [];
    residingCities: City[] = [];
    selectedCountry: Country | undefined;
    selectedResidingCountry: Country | undefined;
    selectedResidingCity: City | undefined;
    selectedLicenseCountry: Country | undefined;
    selectedTransportPermitCountry: Country | undefined;
    selectedHomeLocation: Location | undefined;
    availableVehicles: Vehicle[] = [];
    transporterClients: CrmClient[] = [];
    homeLocationOptions: Location[] = [];
    selectedVehicle: Vehicle | undefined;
    selectedTransporterClient: CrmClient | undefined;
    selectedDateOfBirth: Date | undefined;
    selectedResidenceExpiryDate: Date | undefined;
    selectedLicenseExpiryDate: Date | undefined;
    selectedTransportPermitExpiryDate: Date | undefined;
    driverCreationInProgress: boolean = false;
    pdfUploadProgress: number = 0;
    private currentCompanyId: number | null = null;
    private companyGroupId: number | null = null;

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    formTitle: string;
    isFreelancer: boolean = false;
    isRental: boolean = false;
    phonePlaceholder = 'Phone number';
    phoneMask = 'Phone number';
    genderOptions = [
        { label: 'Male', value: true },
        { label: 'Female', value: false }
    ];
    identityTypeOptions = [
        { label: 'Iqama / Residence ID', value: 'IQAMA' },
        { label: 'National ID', value: 'NATIONAL_ID' },
        { label: 'Passport', value: 'PASSPORT' },
        { label: 'GCC ID', value: 'GCC_ID' }
    ];

    constructor(
        private sessionStorageService: SessionStorageService,
        private route: ActivatedRoute,
        private driverService: DriverService,
        private messageService: MessageService,
        private vehicleService: VehicleService,
        private router: Router,
        private firebaseStorageService: FirebaseStorageService,
        private companyService: CompanyService,
        private transportationAgencyService: TransportationAgencyService,
        private baseMasterDataService: BaseMasterDataService,
        private locationService: LocationService,
        private tenantScopeService: TenantScopeService,
        private crmService: CrmService
    ) {
        this.loadCountries();
    }

    ngOnInit(): void {
        this.createEditDriverForm = new FormGroup({
            nameInput: new FormControl('', Validators.required),
            phoneNumberInput: new FormControl('', Validators.required),
            emailAddressInput: new FormControl(''),
            nationalityDropdown: new FormControl(''),
            residingCountryDropdown: new FormControl('', Validators.required),
            residingCityDropdown: new FormControl('', Validators.required),
            homeLocationDropdown: new FormControl(''),
            dateOfBirthInput: new FormControl(''),
            genderDropdown: new FormControl(''),
            identityTypeInput: new FormControl('IQAMA'),
            identityIssueNumberInput: new FormControl(1),
            residenceNumberInput: new FormControl('', Validators.required),
            residenceExpiryDateInput: new FormControl(''),
            licenseNumberInput: new FormControl(''),
            licenseExpiryDateInput: new FormControl(''),
            licenseCountryDropdown: new FormControl(''),
            licenseClassInput: new FormControl(''),
            licenseTypeInput: new FormControl(''),
            transportPermitNumberInput: new FormControl(''),
            transportPermitExpiryDateInput: new FormControl(''),
            transportPermitCountryDropdown: new FormControl(''),
            currentVehicleDropdown: new FormControl('')
        });
        this.applyPhoneCountryRules(undefined);
        this.currentCompanyId = this.sessionStorageService.getCompany()?.id ?? null;
        this.initializeResourceContext();
        this.loadTransporterClients();

        this.refreshCompanyFleetProfile();

        // Get driver ID from route parameters
        const driverId = this.route.snapshot.paramMap.get('id');

        if (driverId) {
            this.formTitle = 'Update Driver';
            this.driverId = Number(driverId);
            this.initializeScopeAndLoadDriver();
        } else {
            this.formTitle = 'Create Driver';
            this.initializeForNewDriver(); // Initialize for new driver
        }

        this.loadVehicles();
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Drivers', routerLink: '/fleet/drivers' }, { label: driverId ? 'Update' : 'Create' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    }

    initializeForNewDriver() {
        // Initialize for a new driver (if needed)
        this.driver = new Driver();
        if (this.route.snapshot.queryParamMap.get('phoneNumber')) {
            this.driver.phoneNumber = this.route.snapshot.queryParamMap.get('phoneNumber')!;
            this.createEditDriverForm.get('phoneNumberInput')?.setValue(this.driver.phoneNumber);
        }
    }

    loadDriver() {
        this.isLoading = true;
        this.loadError = null;
        this.driverService.findDriverByFilter(
            undefined,
            this.driverId,
            undefined,
            undefined,
            true,
            'en-US',
            this.currentCompanyId ?? undefined,
            this.companyGroupId ?? undefined
        ).subscribe({
            next: driverWrapper => {
                this.driver = driverWrapper.data;
                if (this.driver?.resourceCompanyId && this.currentCompanyId && this.driver.resourceCompanyId !== this.currentCompanyId) {
                    this.blockSisterCompanyEdit();
                    return;
                }
                this.selectedDateOfBirth = this.driver.dateOfBirth ? new Date(this.driver.dateOfBirth) : undefined;
                this.selectedResidenceExpiryDate = this.driver.residenceExpiryDate ? new Date(this.driver.residenceExpiryDate) : undefined;
                this.selectedLicenseExpiryDate = this.driver.licenseExpiryDate ? new Date(this.driver.licenseExpiryDate) : undefined;
                this.selectedTransportPermitExpiryDate = this.driver.transportPermitExpiryDate ? new Date(this.driver.transportPermitExpiryDate) : undefined;
                this.updateFormValues();
                this.isLoading = false;
            },
            error: error => {
                this.loadError = error?.status === 403 && this.route.snapshot.queryParamMap.get('resourceView') === 'sister'
                    ? sisterCompanyFleetAccessMessage('driver')
                    : 'Unable to load this driver for editing.';
                this.isLoading = false;
            }
        });
    }

    private initializeScopeAndLoadDriver(): void {
        const applyScope = () => {
            this.companyGroupId = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.drivers.view']
            )?.companyGroupId ?? null;
            this.loadDriver();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private blockSisterCompanyEdit(): void {
        this.isLoading = false;
        this.messageService.add({
            severity: 'warn',
            summary: 'Read-only record',
            detail: 'Sister-company drivers can be viewed here, but not edited from this company.'
        });
        setTimeout(() => {
            this.router.navigate(['/fleet/driver/view', this.driverId], {
                queryParams: this.route.snapshot.queryParams
            });
        }, 150);
    }

    updateFormValues() {
        this.createEditDriverForm.patchValue({
            nameInput: this.driver.name,
            phoneNumberInput: this.driver.phoneNumber,
            emailAddressInput: this.driver.emailAddress,
            nationalityDropdown: this.driver.nationality,
            residingCountryDropdown: this.driver.residingCountry,
            residingCityDropdown: this.driver.residingCity,
            homeLocationDropdown: this.driver.homeLocation,
            dateOfBirthInput: this.selectedDateOfBirth,
            genderDropdown: this.driver.gender,
            identityTypeInput: this.driver.identityType || 'IQAMA',
            identityIssueNumberInput: this.driver.identityIssueNumber || 1,
            residenceNumberInput: this.driver.residenceNumber,
            residenceExpiryDateInput: this.selectedResidenceExpiryDate,
            licenseNumberInput: this.driver.licenseNumber,
            licenseExpiryDateInput: this.selectedLicenseExpiryDate,
            licenseCountryDropdown: this.driver.licenseCountry || this.driver.residingCountry,
            licenseClassInput: this.driver.licenseClass,
            licenseTypeInput: this.driver.licenseType,
            transportPermitNumberInput: this.driver.transportPermitNumber,
            transportPermitExpiryDateInput: this.selectedTransportPermitExpiryDate,
            transportPermitCountryDropdown: this.driver.transportPermitCountry,
            currentVehicleDropdown: this.driver.currentVehicle
        });

        this.selectedCountry = this.countries.find(c => c.id === this.driver.nationality?.id);
        this.selectedResidingCountry = this.countries.find(c => c.id === this.driver.residingCountry?.id);
        this.selectedLicenseCountry = this.countries.find(c => c.id === this.driver.licenseCountry?.id)
            ?? this.selectedResidingCountry;
        this.selectedTransportPermitCountry = this.countries.find(c => c.id === this.driver.transportPermitCountry?.id);
        this.selectedHomeLocation = this.driver.homeLocation;
        this.selectedVehicle = this.availableVehicles.find(v => v.vehicleId === this.driver.currentVehicle?.vehicleId);
        this.syncSelectedTransporterClient();
        this.applyPhoneCountryRules(this.selectedResidingCountry);
        this.loadResidingCities(this.selectedResidingCountry?.id, this.driver.residingCity?.id);
    }

    private loadTransporterClients(): void {
        if (!this.currentCompanyId || (!this.isFreelancer && !this.isRental)) {
            this.transporterClients = [];
            this.selectedTransporterClient = undefined;
            return;
        }
        this.crmService.getClients(this.currentCompanyId, ['TRANSPORTER']).subscribe(clients => {
            this.transporterClients = clients ?? [];
            this.syncSelectedTransporterClient();
        });
    }

    loadCountries() {
        this.countries = this.sessionStorageService.getCountryList() || [];
    }

    loadResidingCities(countryId?: number, preferredCityId?: number) {
        this.residingCities = [];
        this.selectedResidingCity = undefined;
        this.createEditDriverForm?.get('residingCityDropdown')?.setValue(null, { emitEvent: false });
        if (!countryId) {
            return;
        }
        this.baseMasterDataService.getCities('en-US', countryId).subscribe(cities => {
            this.residingCities = cities ?? [];
            const selectedCity = preferredCityId
                ? this.residingCities.find(city => city.id === preferredCityId)
                : undefined;
            if (selectedCity) {
                this.selectedResidingCity = selectedCity;
                this.createEditDriverForm.get('residingCityDropdown')?.setValue(selectedCity, { emitEvent: false });
                this.driver.residingCity = selectedCity;
                this.applyCityToPreferredLocation(selectedCity);
            }
        });
    }

    applyPhoneCountryRules(country?: Country) {
        this.phonePlaceholder = phonePlaceholderForCountry(country);
        this.phoneMask = phoneMaskForCountry(country);
        const control = this.createEditDriverForm?.get('phoneNumberInput');
        control?.setValidators([Validators.required, countryPhoneValidator(() => this.selectedResidingCountry)]);
        control?.updateValueAndValidity({ emitEvent: false });
    }

    normalizePhoneInput() {
        const normalized = normalizePhoneForCountry(
            this.createEditDriverForm.get('phoneNumberInput')?.value,
            this.selectedResidingCountry);
        if (normalized) {
            this.createEditDriverForm.get('phoneNumberInput')?.setValue(normalized);
        }
    }

    private applyCityToPreferredLocation(city?: City) {
        if (!city) return;
        this.driver.preferredAddress = city.cityName;
        if (city.coordinates) {
            this.driver.preferredLocation = city.coordinates;
        }
    }

    searchHomeLocations(event: AutoCompleteCompleteEvent) {
        const query = (event.query || '').trim();
        this.locationService.getAllComboData(query, 0, 25, 'en-US').subscribe(response => {
            this.homeLocationOptions = response.items ?? [];
            if (this.selectedHomeLocation?.id
                && !this.homeLocationOptions.some(location => location.id === this.selectedHomeLocation?.id)) {
                this.homeLocationOptions = [this.selectedHomeLocation, ...this.homeLocationOptions];
            }
        });
    }

    onHomeLocationChange(event: any) {
        this.selectedHomeLocation = event.value || undefined;
        this.driver.homeLocation = this.selectedHomeLocation;
    }

    loadVehicles() {
        const transportationAgencyId = this.resolveTransportationAgencyId();
        const companyId = this.sessionStorageService.getCompany()?.id;
        const pageSize = 250;
        if (this.isRental && companyId) {
            this.vehicleService.getRentalVehiclesByCompanyId(0, pageSize, companyId)
                .subscribe(response => this.setAvailableVehicles(response.items));
        } else if (this.isFreelancer && companyId) {
            this.vehicleService.getFreelanceVehiclesByCompanyId(0, pageSize, companyId)
                .subscribe(response => this.setAvailableVehicles(response.items));
        } else if (transportationAgencyId) {
            this.vehicleService.findVehiclesByTransportationAgencyId(0, pageSize, transportationAgencyId)
                .subscribe(response => this.setAvailableVehicles(response.items));
        }
    }

    onCountryChange(event: any) {
        this.selectedCountry = event.value;
        if (this.selectedCountry)
            this.driver.nationality = this.selectedCountry;
    }

    onResidingCountryChange(event: any) {
        this.selectedResidingCountry = event.value;
        if (this.selectedResidingCountry) {
            this.driver.residingCountry = this.selectedResidingCountry;
            this.applyPhoneCountryRules(this.selectedResidingCountry);
            this.loadResidingCities(this.selectedResidingCountry.id);
            this.createEditDriverForm.get('licenseCountryDropdown')?.setValue(
                this.createEditDriverForm.get('licenseCountryDropdown')?.value || this.selectedResidingCountry);
        }
    }

    onResidingCityChange(event: any) {
        this.selectedResidingCity = event.value;
        this.driver.residingCity = this.selectedResidingCity;
        this.applyCityToPreferredLocation(this.selectedResidingCity);
    }

    onLicenseCountryChange(event: any) {
        this.selectedLicenseCountry = event.value;
        this.driver.licenseCountry = this.selectedLicenseCountry;
    }

    onTransportPermitCountryChange(event: any) {
        this.selectedTransportPermitCountry = event.value;
        this.driver.transportPermitCountry = this.selectedTransportPermitCountry;
    }

    onVehicleChange(event: any) {
        this.selectedVehicle = event.value;
        this.driver.currentVehicle = this.selectedVehicle;
    }

    onTransporterClientChange(event: any): void {
        this.selectedTransporterClient = event.value || undefined;
        this.driver.transporterCrmClientId = this.selectedTransporterClient?.id;
    }

    saveDriver() {
        if (this.isRental && !this.driver.id) {
            this.messageService.add({
                severity: 'info',
                summary: 'Create rental assets from an agreement',
                detail: 'Rental drivers must be added from Fleet > Rental > Agreements so the agreement, rate card, and effective dates are captured together.'
            });
            if (this.embedded) {
                this.cancelled.emit();
            } else {
                this.router.navigate(['/fleet/rentals/agreements']);
            }
            return;
        }

        if (this.createEditDriverForm.invalid) {
            this.createEditDriverForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please complete the required driver fields before saving.'
            });
            return;
        }

        this.syncDriverFromForm();
        this.driverCreationInProgress = true;
        this.driver.legalName = this.driver.name;
        this.driver.dateOfBirth = this.selectedDateOfBirth as any;
        this.driver.residenceExpiryDate = this.selectedResidenceExpiryDate?.getTime() ?? new Date().getTime();
        this.driver.licenseExpiryDate = this.selectedLicenseExpiryDate?.getTime() ?? new Date().getTime();
        this.driver.transportPermitExpiryDate = this.selectedTransportPermitExpiryDate?.getTime();
        if (this.isFreelancer) {
            this.driverService.addFreelanceDriver(this.driver, this.sessionStorageService.getCompany()!.id)
                .subscribe(response => {
                    if (response.success) {
                        this.messageService.add({
                            severity: 'success', summary: 'Driver ' + this.driver.name + ' is updated',
                            detail: 'Driver ' + this.driver.name + ' has been successfully updated'
                        });
                        this.handleSuccessfulSave(this.driver);
                    } else {
                        this.showDriverSaveError(response, 'update');
                    }
                    this.driverCreationInProgress = false;
                }, error => {
                    this.showDriverSaveError(error, 'update');
                    this.driverCreationInProgress = false;
                });
        }
        else if (this.driver.id) {
            this.resolveTransportationAgencyForDriver(agency => {
                if (!agency) {
                    this.showMissingTransportationAgencyError();
                    return;
                }
                this.driver.currentTransportationAgency = agency;
                this.driverService.updateDriver(this.driver).subscribe(response => {
                    if (response.success) {
                        this.messageService.add({
                            severity: 'success', summary: 'Driver ' + response.data.name + ' is updated',
                            detail: 'Driver ' + response.data.name + ' has been successfully updated'
                        });
                        this.handleSuccessfulSave(response.data);
                    } else {
                        this.showDriverSaveError(response, 'update');
                    }
                    this.driverCreationInProgress = false;
                }, error => {
                    this.showDriverSaveError(error, 'update');
                    this.driverCreationInProgress = false;
                });
            });
        } else {
            this.resolveTransportationAgencyForDriver(agency => {
                if (!agency) {
                    this.showMissingTransportationAgencyError();
                    return;
                }
                this.driver.currentTransportationAgency = agency;
                this.driverService.addDriver(this.driver).subscribe(response => {
                    if (response.success) {
                        this.messageService.add({
                            severity: 'success', summary: 'Driver ' + response.data.name + ' is created',
                            detail: 'Driver ' + response.data.name + ' has been successfully created'
                        });
                        this.handleSuccessfulSave(response.data);
                    } else {
                        this.showDriverSaveError(response, 'create');
                    }
                    this.driverCreationInProgress = false;
                }, error => {
                    this.showDriverSaveError(error, 'create');
                    this.driverCreationInProgress = false;
                });
            });
        }
    }

    cancelEmbedded(): void {
        this.cancelled.emit();
    }

    private handleSuccessfulSave(driver: Driver): void {
        if (this.embedded) {
            this.saved.emit(driver);
            return;
        }
        setTimeout(() => this.router.navigate(['/fleet/drivers']), 2000);
    }

    private refreshCompanyFleetProfile(): void {
        const company = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
        if (!company?.id || company.transportationAgencyId) {
            return;
        }

        this.companyService.getCompanyById(company.id).subscribe({
            next: response => {
                if (response.success && response.data) {
                    this.sessionStorageService.saveCompany(response.data);
                    this.refreshTransportationAgency(response.data.transportationAgencyId ?? undefined);
                    this.loadVehicles();
                }
            }
        });
    }

    private resolveTransportationAgencyId(): number | undefined {
        return this.driver.currentTransportationAgency?.rowId
            ?? this.sessionStorageService.getCompany()?.transportationAgencyId
            ?? this.sessionStorageService.getCustomer()?.company?.transportationAgencyId
            ?? this.sessionStorageService.getTransportationAgency()?.rowId
            ?? undefined;
    }

    private resolveTransportationAgencyForDriver(done: (agency?: TransportationAgency) => void): void {
        const sessionAgency = this.sessionStorageService.getTransportationAgency();
        const agencyId = this.resolveTransportationAgencyId();
        if (sessionAgency?.rowId && (!agencyId || sessionAgency.rowId === agencyId)) {
            done(sessionAgency);
            return;
        }
        if (agencyId) {
            this.refreshTransportationAgency(agencyId, done);
            return;
        }

        const companyId = this.sessionStorageService.getCompany()?.id ?? this.sessionStorageService.getCustomer()?.company?.id;
        if (!companyId) {
            done(undefined);
            return;
        }

        this.companyService.getCompanyById(companyId).subscribe({
            next: response => {
                if (response.success && response.data) {
                    this.sessionStorageService.saveCompany(response.data);
                    this.refreshTransportationAgency(response.data.transportationAgencyId ?? undefined, done);
                    return;
                }
                done(undefined);
            },
            error: () => done(undefined)
        });
    }

    private refreshTransportationAgency(agencyId?: number, done?: (agency?: TransportationAgency) => void): void {
        if (!agencyId) {
            done?.(undefined);
            return;
        }

        this.transportationAgencyService.findTransportationAgencyByFilter(undefined, undefined, agencyId).subscribe({
            next: response => {
                const agency = response.success ? response.data : undefined;
                if (agency) {
                    this.sessionStorageService.saveTransportationAgency(agency);
                }
                done?.(agency);
            },
            error: () => done?.(undefined)
        });
    }

    private showMissingTransportationAgencyError(): void {
        this.driverCreationInProgress = false;
        this.messageService.add({
            severity: 'error',
            summary: 'Transportation agency missing',
            detail: 'This company is not linked to a transportation agency. Refresh the session or complete company fleet setup before saving a driver.'
        });
    }

    /**
     * The API returns structured error details for conflicts. Do not expose the
     * raw HttpErrorResponse because it contains a technical endpoint URL and
     * hides the field the user needs to correct.
     */
    private showDriverSaveError(error: any, action: 'create' | 'update'): void {
        const serverError = this.getDriverSaveError(error);
        const actionLabel = action === 'update' ? 'update' : 'create';

        this.messageService.add({
            severity: 'error',
            summary: action === 'update' ? 'Driver update blocked' : 'Driver could not be created',
            detail: this.getDriverSaveErrorDetail(serverError, actionLabel)
        });
    }

    private getDriverSaveError(error: any): { status?: number; message?: string; field?: string } {
        let payload = error?.error ?? error;

        if (typeof payload === 'string') {
            try {
                payload = JSON.parse(payload);
            } catch {
                return { status: error?.status, message: payload };
            }
        }

        return {
            status: payload?.status ?? error?.status,
            message: payload?.message,
            field: payload?.details?.field
        };
    }

    private getDriverSaveErrorDetail(
        error: { status?: number; message?: string; field?: string },
        action: string
    ): string {
        if (error.status === 409) {
            if (error.field === 'phoneNumber') {
                return 'This driver profile is linked to a different sign-in account than the one that owns this phone number. The driver was not changed. Ask an administrator to reconcile the two authentication accounts before saving.';
            }
            if (error.field === 'emailAddress') {
                return 'This driver profile is linked to a different sign-in account than the one that owns this email address. The driver was not changed. Ask an administrator to reconcile the two authentication accounts before saving.';
            }
            if (error.message) {
                return error.message;
            }
            return `This driver cannot be ${action}d because one of the unique details is already in use. Check the phone number, email address, licence number, residence number, and assigned vehicle.`;
        }

        if (error.message && !error.message.startsWith('Http failure response')) {
            return error.message;
        }

        return `We could not ${action} this driver. Please try again, and contact support if the problem continues.`;
    }

    isFieldInvalid(controlName: string): boolean {
        const control = this.createEditDriverForm?.get(controlName);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    getFieldError(controlName: string): string {
        const control = this.createEditDriverForm?.get(controlName);
        if (control?.hasError('required')) {
            return 'This field is required.';
        }
        if (control?.hasError('invalidPhoneForCountry')) {
            return `Enter a valid phone number for ${this.selectedResidingCountry?.name || 'the selected country'}.`;
        }
        return '';
    }

    private setAvailableVehicles(vehicles: Vehicle[] = []): void {
        this.availableVehicles = vehicles;
        if (this.driver.currentVehicle) {
            this.selectedVehicle = this.availableVehicles.find(v => v.vehicleId === this.driver.currentVehicle?.vehicleId);
        }
    }

    private syncDriverFromForm(): void {
        const formValue = this.createEditDriverForm.getRawValue();
        this.driver.name = formValue.nameInput;
        this.driver.phoneNumber = normalizePhoneForCountry(formValue.phoneNumberInput, formValue.residingCountryDropdown)
            || formValue.phoneNumberInput;
        this.driver.emailAddress = formValue.emailAddressInput;
        this.driver.nationality = formValue.nationalityDropdown;
        this.driver.residingCountry = formValue.residingCountryDropdown;
        this.driver.residingCity = formValue.residingCityDropdown;
        this.applyCityToPreferredLocation(this.driver.residingCity);
        this.driver.homeLocation = formValue.homeLocationDropdown || undefined;
        this.driver.gender = formValue.genderDropdown;
        this.driver.identityType = formValue.identityTypeInput;
        this.driver.identityIssueNumber = formValue.identityIssueNumberInput;
        this.driver.residenceNumber = formValue.residenceNumberInput;
        this.driver.licenseNumber = formValue.licenseNumberInput;
        this.driver.licenseCountry = formValue.licenseCountryDropdown;
        this.driver.licenseClass = formValue.licenseClassInput;
        this.driver.licenseType = formValue.licenseTypeInput;
        this.driver.transportPermitNumber = formValue.transportPermitNumberInput;
        this.driver.transportPermitCountry = formValue.transportPermitCountryDropdown;
        this.selectedDateOfBirth = formValue.dateOfBirthInput;
        this.selectedResidenceExpiryDate = formValue.residenceExpiryDateInput;
        this.selectedLicenseExpiryDate = formValue.licenseExpiryDateInput;
        this.selectedTransportPermitExpiryDate = formValue.transportPermitExpiryDateInput;
        this.selectedCountry = formValue.nationalityDropdown;
        this.selectedResidingCountry = formValue.residingCountryDropdown;
        this.selectedResidingCity = formValue.residingCityDropdown;
        this.selectedLicenseCountry = formValue.licenseCountryDropdown;
        this.selectedTransportPermitCountry = formValue.transportPermitCountryDropdown;
        this.selectedHomeLocation = formValue.homeLocationDropdown || undefined;
        this.selectedVehicle = formValue.currentVehicleDropdown || undefined;
        this.driver.currentVehicle = this.selectedVehicle;
        this.driver.transporterCrmClientId = this.selectedTransporterClient?.id;
    }

    clearForm() {
        this.createEditDriverForm.reset();
        this.driver = new Driver();
        this.selectedCountry = undefined;
        this.selectedResidingCountry = undefined;
        this.selectedResidingCity = undefined;
        this.selectedLicenseCountry = undefined;
        this.selectedTransportPermitCountry = undefined;
        this.selectedHomeLocation = undefined;
        this.homeLocationOptions = [];
        this.selectedVehicle = undefined;
        this.selectedTransporterClient = undefined;
        this.residingCities = [];
    }

    private initializeResourceContext(): void {
        if (this.embedded) {
            this.isFreelancer = this.embeddedSource === 'freelance';
            this.isRental = this.embeddedSource === 'rental';
        }
        const source = this.route.snapshot.queryParamMap.get('source');
        if (source === 'freelance') {
            this.isFreelancer = true;
            this.isRental = false;
        } else if (source === 'rental') {
            this.isRental = true;
            this.isFreelancer = false;
        } else if (source === 'owned') {
            this.isFreelancer = false;
            this.isRental = false;
        }
        if (this.route.snapshot.queryParamMap.get('isFreelancer')) {
            this.isFreelancer = this.route.snapshot.queryParamMap.get('isFreelancer') === 'true';
        }
        if (this.route.snapshot.queryParamMap.get('isRental')) {
            this.isRental = this.route.snapshot.queryParamMap.get('isRental') === 'true';
        }
    }

    private syncSelectedTransporterClient(): void {
        this.selectedTransporterClient = this.transporterClients.find(client => client.id === this.driver.transporterCrmClientId);
    }

    onResidenceCardUpload(event: FileSelectEvent) {
        console.log(event.files);
        this.uploadPDFFile(event.files[0], 'RESIDENCE');
    }

    onLicenseCardUpload(event: FileSelectEvent) {
        console.log(event.files);
        this.uploadPDFFile(event.files[0], 'LICENSE');
    }

    uploadPDFFile(file: File, fileType: string) {
        this.driverCreationInProgress = true;
        this.pdfUploadProgress = 0;

        const storagePath = `drivers/${fileType === 'RESIDENCE' ? 'residence_scan' : 'license_scan'}`;

        this.firebaseStorageService.uploadFileWithProgress(file, storagePath).subscribe({
            next: (upload) => {
                this.pdfUploadProgress = upload.progress;
                console.log(`Upload is ${upload.progress}% done`);

                if (upload.url) {
                    console.log('File available at', upload.url);
                    if (fileType === 'RESIDENCE') {
                        this.driver.residenceImage = upload.url;
                    } else {
                        this.driver.licenseImage = upload.url;
                    }
                    this.driverCreationInProgress = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully.' });
                }
            },
            error: (err) => {
                console.error("Error uploading file:", err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'PDF upload failed.' });
                this.driverCreationInProgress = false;
                this.pdfUploadProgress = 0;
            }
        });
    }

}
